import { ISalePageDetailV2Response } from '@/api/types';
import { FundingStatus } from '@/entities/funding/components';
import { Column, Element, Icon, Row, Skeleton } from '@vuddy/components';
import { SalePageInstructions } from '../instructions';
import { SalePageTags } from '../tags';
import * as styles from './summaryContents.css';
import SummaryCTA from './SummaryCTA';

const SummaryContents = ({
  salePageId,
  salePage,
  hidePurchaseCTAButton,
  isShowSummaryCTA,
}: {
  salePageId?: number;
  salePage?: Partial<ISalePageDetailV2Response>;
  hidePurchaseCTAButton: boolean;
  isShowSummaryCTA: boolean;
}) => {
  if (!salePage) return null;

  // 펀딩 여부
  const isFunding = salePage?.type === 'FUNDING';

  // 2차 창작자 부모 채널명
  const secondaryCreatorParentChannelName =
    salePage?.channel?.parentChannel?.name;

  // 웹에서만 구매 가능
  const shouldShowWebOnlyPurchaseNotice = Boolean(
    hidePurchaseCTAButton &&
    salePage?.type === 'DIGITAL' &&
    salePage?.products?.some(product => product.type === 'CUSTOM_MEDIA')
  );

  return (
    <Column
      gap={isFunding ? 'gap_2xl' : 'gap_4xl'}
      className={styles.summaryContentWrapper}>
      <Column gap="gap_large">
        <SalePageTags salePage={salePage} />
        {/* 타이틀 영역 */}
        <Element className={styles.title} typography="titleSmall">
          {salePage?.name ?? <Skeleton />}
        </Element>

        {/* 가격 영역 - 클래스를 제외한 나머지 상품 */}
        {!isFunding && (
          <Column gap="gap_2xs">
            <Element typography="titleBase" color="vuddyPrimary">
              {salePage?.priceForDisplay ?? <Skeleton />}
            </Element>
          </Column>
        )}

        {/* 2차 창작자 안내 문구 */}
        {secondaryCreatorParentChannelName && (
          <Element
            backgroundColor="secondary"
            borderRadius="radius_base"
            padding="padding_xs">
            <Row gap="gap_xs" alignItems="flex-start">
              <Element display="flex" style={{ flexShrink: 0 }}>
                <Icon name="FILL_FAN_CREATOR" size="16px" color="tertiary" />
              </Element>
              <Element typography="captionBaseAccent" color="tertiary">
                {secondaryCreatorParentChannelName}의 공식 승인을 받은 2차
                창작자가 제작한 상품입니다.
              </Element>
            </Row>
          </Element>
        )}
      </Column>

      {/* 펀딩 가격 영역 */}
      {isFunding && (
        <FundingStatus
          amount={salePage.funding?.currentAmount ?? 0}
          achievementRate={salePage.funding?.achievementRate ?? 0}
          saleStatus={salePage.saleStatus ?? 'PLANNED'}
          daysLeft={salePage.funding?.leftDay ?? 0}
        />
      )}

      <SalePageInstructions salePage={salePage} />

      {shouldShowWebOnlyPurchaseNotice && (
        <Element
          backgroundColor="tertiary"
          borderRadius="radius_base"
          paddingY="padding_2xs"
          paddingX="padding_xs">
          <Row gap="gap_xs" alignItems="center">
            <Icon name="FILL_INFORMATION_CIRCLE" size="16px" color="tertiary" />
            <Element typography="captionBaseAccent" color="tertiary">
              웹에서만 구매 가능합니다.
            </Element>
          </Row>
        </Element>
      )}
      {isShowSummaryCTA && salePageId && <SummaryCTA salePageId={salePageId} />}
    </Column>
  );
};

export default SummaryContents;
